import * as React from "react";
import {useId, useState} from "react";
import {PROVISIONAL_HATCH_STYLE} from "./provisional";
import {LABEL_HEIGHT, niceTicks, tooltipPosition, useChartWidth, ValueAxis} from "./chartAxis";

export type StackedBarColumn = {
    key: string
    label: string
    isProvisional?: boolean
};

export type StackedBarSeries = {
    id: string
    label: string
    color: string // CSS color, the same for the entity wherever it appears
    values: number[] // one per column, in the order of the columns
};

type StackedBarChartProps = {
    columns: StackedBarColumn[]
    series: StackedBarSeries[]
    formatAmount: (amount: number) => string
    height?: number
    totalLabel: string
    provisionalLegendLabel?: string // shows the hatching in the legend if given
    selectedColumnKey?: string // highlights the column
    onSelectColumn?: (key: string) => void // makes the columns clickable
};

const AXIS_WIDTH = 64;
const TOP_PADDING = 8;
const SEGMENT_GAP = 2;

/**
 * A plain stacked bar chart with one bar per column, e.g. per month, stacked by series. Negative
 * values, such as credit notes, are stacked below the zero line. Provisional columns are hatched
 * and lighter. Hovering a column shows its values.
 * If onSelectColumn is given, clicking a column selects it.
 */
const StackedBarChart: React.FC<StackedBarChartProps> = ({columns, series, formatAmount, height = 220, totalLabel, provisionalLegendLabel, selectedColumnKey, onSelectColumn}) => {
    const [containerRef, width] = useChartWidth();
    const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);
    const patternId = 'hatch-' + useId().replace(/:/g, '');

    const positiveTotals = columns.map((_, index) => series.reduce((sum, entry) => sum + Math.max(0, entry.values[index] ?? 0), 0));
    const negativeTotals = columns.map((_, index) => series.reduce((sum, entry) => sum + Math.min(0, entry.values[index] ?? 0), 0));
    const ticks = niceTicks(Math.min(0, ...negativeTotals), Math.max(0, ...positiveTotals));
    const domainMinimum = ticks[0];
    const domainMaximum = ticks[ticks.length - 1];

    const plotHeight = height - LABEL_HEIGHT - TOP_PADDING;
    const plotWidth = Math.max(0, width - AXIS_WIDTH);
    const band = columns.length > 0 ? plotWidth / columns.length : 0;
    const barWidth = Math.min(48, band * 0.6);
    const y = (value: number) => TOP_PADDING + plotHeight - ((value - domainMinimum) / (domainMaximum - domainMinimum || 1)) * plotHeight;

    const hovered = hoveredColumn !== null ? columns[hoveredColumn] : null;

    return (
        <div>
            <div ref={containerRef} className="relative" style={{height}} onMouseLeave={() => setHoveredColumn(null)}>
                {width > 0 && (
                    <svg width={width} height={height} role="img" aria-label={series.map(entry => entry.label).join(', ')}>
                        <defs>
                            <pattern id={patternId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                                <line x1="0" y1="0" x2="0" y2="6" stroke="white" strokeWidth="2.5" strokeOpacity="0.7"/>
                            </pattern>
                        </defs>
                        <ValueAxis ticks={ticks} axisWidth={AXIS_WIDTH} width={width} y={y} formatAmount={formatAmount}/>
                        {columns.map((column, index) => {
                            const x = AXIS_WIDTH + index * band + (band - barWidth) / 2;
                            let positiveBase = 0;
                            let negativeBase = 0;
                            const segments = series.map(entry => {
                                const value = entry.values[index] ?? 0;
                                if (value === 0) {
                                    return null;
                                }
                                const from = value > 0 ? positiveBase : negativeBase;
                                const to = from + value;
                                if (value > 0) {
                                    positiveBase = to;
                                } else {
                                    negativeBase = to;
                                }
                                const top = y(Math.max(from, to));
                                const segmentHeight = Math.abs(y(from) - y(to));
                                const gap = segmentHeight > SEGMENT_GAP * 2 ? SEGMENT_GAP : 0;
                                return {id: entry.id, color: entry.color, top: value > 0 ? top + gap : top, height: segmentHeight - gap};
                            }).filter(segment => segment !== null);
                            const isDimmed = hoveredColumn !== null && hoveredColumn !== index;
                            const isSelected = column.key === selectedColumnKey;
                            return (
                                <g key={column.key} opacity={isDimmed ? 0.45 : column.isProvisional ? 0.7 : 1}>
                                    {isSelected && <rect x={AXIS_WIDTH + index * band} y={0} width={band} height={height} fill="#eff6ff"/>}
                                    {segments.map(segment => (
                                        <rect key={segment.id} x={x} y={segment.top} width={barWidth} height={Math.max(0, segment.height)} fill={segment.color}/>
                                    ))}
                                    {column.isProvisional && segments.map(segment => (
                                        <rect key={segment.id + '-hatch'} x={x} y={segment.top} width={barWidth} height={Math.max(0, segment.height)} fill={`url(#${patternId})`}/>
                                    ))}
                                    <text x={AXIS_WIDTH + index * band + band / 2} y={height - 6} textAnchor="middle" className={`text-[11px] ${isSelected ? 'fill-blue-700 font-semibold' : 'fill-gray-500'} ${column.isProvisional ? 'italic' : ''}`}>{column.label}</text>
                                    <rect
                                        x={AXIS_WIDTH + index * band}
                                        y={0}
                                        width={band}
                                        height={height}
                                        fill="transparent"
                                        className={onSelectColumn ? 'cursor-pointer' : undefined}
                                        onMouseEnter={() => setHoveredColumn(index)}
                                        onClick={onSelectColumn ? () => onSelectColumn(column.key) : undefined}
                                    />
                                </g>
                            );
                        })}
                    </svg>
                )}
                {hovered && hoveredColumn !== null && (
                    <div
                        className="pointer-events-none absolute top-0 z-20 min-w-48 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg"
                        style={tooltipPosition(hoveredColumn, columns.length, band, AXIS_WIDTH, width)}
                    >
                        <div className={`mb-1 font-medium text-gray-900 ${hovered.isProvisional ? 'italic' : ''}`}>{hovered.label}</div>
                        {[...series].reverse().filter(entry => (entry.values[hoveredColumn] ?? 0) !== 0).map(entry => (
                            <div key={entry.id} className="flex items-center gap-2 text-gray-700">
                                <span className="h-2 w-2 shrink-0 rounded-sm" style={{backgroundColor: entry.color}}/>
                                <span className="flex-1 truncate">{entry.label}</span>
                                <span className="tabular-nums">{formatAmount(entry.values[hoveredColumn] ?? 0)}</span>
                            </div>
                        ))}
                        <div className="mt-1 flex justify-between gap-4 border-t border-gray-100 pt-1 font-medium text-gray-900">
                            <span>{totalLabel}</span>
                            <span className="tabular-nums">{formatAmount(positiveTotals[hoveredColumn] + negativeTotals[hoveredColumn])}</span>
                        </div>
                    </div>
                )}
            </div>
            <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600" style={{paddingLeft: AXIS_WIDTH}}>
                {series.map(entry => (
                    <li key={entry.id} className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-sm" style={{backgroundColor: entry.color}}/>
                        {entry.label}
                    </li>
                ))}
                {provisionalLegendLabel && (
                    <li className="flex items-center gap-1.5 italic">
                        <span className="h-2.5 w-4 rounded-sm border border-gray-300" style={PROVISIONAL_HATCH_STYLE}/>
                        {provisionalLegendLabel}
                    </li>
                )}
            </ul>
        </div>
    );
};

export default StackedBarChart;
